import { yupResolver } from "@hookform/resolvers/yup";
import { FunctionComponent } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button } from "src/components/generic";
import { TextInputWithRegister } from "src/components/inputs";
import { auth, dynamo, format, forms } from "src/utils";

const ReferralPartnerRegistrationForm: FunctionComponent = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      companyName: "",
      representativeName: "",
      websiteURL: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      phone: "",
      email: "",
      password: "",
      reasonForPartnership: "",
      sourceOfClients: "",
      clientsPerMonth: "",
      howDidYouHear: "",
      expectations: "",
      references: "",
    },
    resolver: yupResolver(forms.schema.ReferralPartnerRegistrationSchema),
  });

  const navigate = useNavigate();

  const save = async (data: any) => {
    const { email, password } = data;
    try {
      const cognitoResult = await auth.cognito.registerCognitoUser(
        email,
        password
      );

      const uid =
        cognitoResult?.userSub ||
        `${Math.random().toFixed(4)}-${Math.random().toFixed(4)}`.replaceAll(
          ".",
          "e"
        );

      const userData = {
        companyName: data.companyName,
        representativeName: data.representativeName,
        websiteURL: data.websiteURL,
        address: data.address,
        city: data.city,
        state: data.state,
        zip: data.zip,
        phone: data.phone,
        email: data.email,
      };

      const metadata = {
        reasonForPartnership: data.reasonForPartnership,
        expectations: data.expectations,
        sourceOfClients: data.sourceOfClients,
        references: data.references,
        clientsPerMonth: data.clientsPerMonth,
        howDidYouHear: data.howDidYouHear,
      };

      const referralPartner = format.users.userDataToReferralPartner(
        uid,
        userData,
        metadata
      );

      console.log({ referralPartner, userData, metadata });
      await dynamo.mutations.putUser(referralPartner);

      navigate(`/auth/confirm-account?email=${referralPartner.userInfo.email}`);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <form
      className="
        max-w-5xl mx-auto bg-white shadow-lg rounded-xl p-8 
        border border-gray-200 
      "
    >
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">
        Referral Partner Registration
      </h2>

      {/* GRID LAYOUT */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TextInputWithRegister
          id="companyName"
          register={register("companyName")}
          label="Company Name"
          placeholder="Company name..."
          error={errors.companyName}
        />

        <TextInputWithRegister
          id="representativeName"
          register={register("representativeName")}
          label="Representative Name"
          placeholder="Representative name..."
          error={errors.representativeName}
        />

        <TextInputWithRegister
          id="email"
          register={register("email")}
          label="Representative Email"
          placeholder="Email..."
          error={errors.email}
        />

        <TextInputWithRegister
          id="password"
          register={register("password")}
          label="Set Password"
          placeholder="Password..."
          error={errors.password}
          password
          showPasswordToggle
        />

        <TextInputWithRegister
          id="phone"
          register={register("phone", {
            onChange: (e) => {
              let value = e.target.value.replace(/\D/g, ""); // keep only numbers
              if (value.length > 10) value = value.slice(0, 10);

              if (value.length > 6) {
                value = `(${value.slice(0, 3)})${value.slice(
                  3,
                  6
                )}-${value.slice(6)}`;
              } else if (value.length > 3) {
                value = `(${value.slice(0, 3)})${value.slice(3)}`;
              } else if (value.length > 0) {
                value = `(${value}`;
              }

              e.target.value = value;
            },
          })}
          label="Company Phone Number"
          placeholder="Phone..."
          error={errors.phone}
        />

        <TextInputWithRegister
          id="websiteURL"
          register={register("websiteURL")}
          label="Company Website Link"
          placeholder="Website URL..."
          error={errors.websiteURL}
        />

        <TextInputWithRegister
          id="address"
          register={register("address")}
          label="Company Address"
          placeholder="Company Address..."
          error={errors.address}
        />

        <TextInputWithRegister
          id="city"
          register={register("city")}
          label="City"
          placeholder="City..."
          error={errors.city}
        />

        <TextInputWithRegister
          id="state"
          register={register("state", {
            onChange: (e) => {
              let value = e.target.value;
              if (value.length > 2) value = value.slice(0, 2);

              e.target.value = value;
            },
          })}
          label="State"
          placeholder="State..."
          error={errors.state}
        />

        <TextInputWithRegister
          id="zip"
          register={register("zip", {
            onChange: (e) => {
              let value = e.target.value;
              if (value.length > 5) value = value.slice(0, 5);

              e.target.value = value;
            },
          })}
          label="Zip Code"
          placeholder="Zip..."
          error={errors.zip}
        />

        <TextInputWithRegister
          id="reasonForPartnership"
          register={register("reasonForPartnership")}
          label="Reason for Partnership"
          placeholder="..."
          error={errors.reasonForPartnership}
        />

        <TextInputWithRegister
          id="sourceOfClients"
          register={register("sourceOfClients")}
          label="Source of Clients"
          placeholder="Company, personal, partners..."
          error={errors.sourceOfClients}
        />

        <TextInputWithRegister
          id="clientsPerMonth"
          register={register("clientsPerMonth")}
          label="Estimated Clients Referred per Month"
          placeholder="X..."
          error={errors.clientsPerMonth}
        />

        <TextInputWithRegister
          id="howDidYouHear"
          register={register("howDidYouHear")}
          label="How Did You Hear About ClientVitals"
          placeholder="..."
          error={errors.howDidYouHear}
        />

        <TextInputWithRegister
          id="expectations"
          register={register("expectations")}
          label="Expectations From Partnership"
          placeholder="..."
          error={errors.expectations}
        />

        <TextInputWithRegister
          id="references"
          register={register("references")}
          label="References"
          placeholder="..."
          error={errors.references}
        />
      </div>

      <div className="mt-10 flex items-center justify-end">
        <a
          className="text-primary hover:underline"
          target="_blank"
          rel="noopener noreferrer"
          href="https://www.dropbox.com/scl/fi/ueb58s7eli31z6jfry3ke/ToS.pdf?rlkey=nq3erxn19cj0x808l7fapfb7t&st=aaexcp2s&dl=0"
        >
          <p className="mr-4">
            By Creating An Account You Agree to ClientVitals' Terms and
            Conditions
          </p>
        </a>
        <Button text="Create Account" onClick={handleSubmit(save)} />
      </div>
    </form>
  );
};

export default ReferralPartnerRegistrationForm;
